import React, {useCallback} from 'react';

import {
  ButtonAppearance,
  ButtonCustom,
  buttonPaddingGenerator,
  TextAlignment,
  TextAppearance,
  TextColor,
  TextCustom,
  TextCustomProps,
  TextLineHeight,
  TextSize,
} from '@vw-marketing/us-components';

import {useFeatureAppConfig} from '../../hooks/use-feature-app-config';
import {InteractionLayerSizeV2} from '@volkswagen-onehub/layer-manager';
import {useFeatureAppEnvironment} from '../../hooks/use-feature-app-environment';
import {useFeatureServices} from '../../hooks/use-feature-services';
import {useTranslations} from '../../hooks/use-translations';
import LineWrapper from '../line-wrapper';
import PaymentContainer from '../payment-container';
import {UsedVehicle} from '../../typing/used-inventory';
import { StyledButtons, StyledContent } from './styles';

interface EstimatePaymentProps {
  vehicle: UsedVehicle;
  zipcode: string;
}

export const EstimatePayment = ({vehicle, zipcode}: EstimatePaymentProps) => {
  const translations = useTranslations().detailsSection.estimatePaymentBox;
  const {
    'layer-manager': layerManager,
    'featureapp-service': featureAppService,
  } = useFeatureServices();
  const {featureAppId: parentFeatureAppId} = useFeatureAppEnvironment();
  const {opeFAName = ''} = useFeatureAppConfig();
  const featureAppConfiguration =
    featureAppService?.featureAppConfigurations[opeFAName];

  const PaymentContainerBridge = useCallback(
    (state, close, options) => {
      const featureAppId = `${parentFeatureAppId}-${featureAppConfiguration?.name}`;

      const layerSetup = {
        featureAppId,
        ...featureAppConfiguration,
      };

      return (
        <PaymentContainer
          state={state}
          close={close}
          options={options}
          vehicleModel={vehicle}
          layerSetup={layerSetup}
          zipcode={zipcode}
        />
      );
    },
    [vehicle, parentFeatureAppId, featureAppConfiguration],
  );

  const openInteractionLayer = useCallback(
    (index: number, size: InteractionLayerSizeV2) => {
      layerManager.openInteractionLayer(
        PaymentContainerBridge,
        {
          text: 'interaction layer',
          index: index + 1,
          canOpenInteraction: true,
          canOpenFocus: true,
          canOpenAlert: true,
          canOpenContent: false,
          canOpenDisclaimer: true,
        },
        {size},
      );
    },
    [layerManager],
  );

  const onHandleEstimatePayment = () => {
    openInteractionLayer(0, InteractionLayerSizeV2.B);
  };

  const textStyle: TextCustomProps = {
    appearance: TextAppearance.copy200,
    style: {
      fontSize: TextSize.TextSize14,
      lineHeight: TextLineHeight.TextLineHeight24,
      color: TextColor.inherit,
      textAlign: TextAlignment.left,
    },
  };

  return (
    <LineWrapper label={translations.lineWraper} variant={true}>
      <StyledContent>
        <TextCustom {...textStyle}>{translations.description}</TextCustom>
      </StyledContent>
        <StyledButtons>
          {/* @ts-ignore */}
          <ButtonCustom
            appearance={ButtonAppearance.Primary}
            onClick={onHandleEstimatePayment.bind(null)}
            padding={buttonPaddingGenerator(10, 10, 10, 21)}
            stretchContent={true}
          >
            <TextCustom
              appearance={TextAppearance.copy100}
              style={{
                color: TextColor.inherit,
              }}
            >
              {translations.paymentCalcCTA}
            </TextCustom>
          </ButtonCustom>
        </StyledButtons>
    </LineWrapper>
  );
};
